<script setup lang="ts">
/**
 * `_ProgressView.vue` — base full-screen progress overlay for long-running
 * operations.
 *
 * Mirrors Unity's `ProgressViewModel` + the `LoadingPanel` chrome shared
 * by `LoadProjectProgressView.prefab`, `UnloadProjectProgressView.prefab`,
 * `DeleteProjectProgressView.prefab`, etc.: full-screen blurred backdrop
 * + a centered ~500×200 panel containing a title, an animated progress
 * bar, the percentage label, an optional log line, and an optional
 * Cancel button. The progress content floats directly over the full-screen
 * overlay, without dialog/panel chrome.
 *
 * Props mirror the ViewModel surface:
 *   - `text`        — main title line ("Loading {name}", etc).
 *   - `percentage`  — 0–100. The bar visibly tweens toward this value
 *                     so consumers don't need to interpolate themselves.
 *   - `logText`     — secondary status line, right-aligned opposite the
 *                     percentage. Both load overlays put the asset-download
 *                     byte readout here ("5.5 of 492 MB"); it is empty
 *                     whenever there is nothing to report, and the meta row
 *                     reserves its height so the panel never reflows when it
 *                     appears.
 *   - `canCancel`   — show the Cancel button.
 *   - `isCancelling`— freeze the bar + flip the title to "Canceling...".
 */
import { computed, onBeforeUnmount, ref, watch } from "vue";
import Button from "@components/controls/Button.vue";

defineOptions({ name: "_ProgressView" });

const props = withDefaults(
  defineProps<{
    text: string;
    percentage?: number;
    logText?: string;
    canCancel?: boolean;
    isCancelling?: boolean;
  }>(),
  { percentage: 0, canCancel: false, isCancelling: false },
);
const emit = defineEmits<{ (e: "cancel"): void }>();

// Tween `percentage` toward `targetPercentage`, mirroring Unity's
// `ProgressViewModel.Update` lerp. Keeps the bar from snapping when
// `onLoadProjectProgress` events arrive in chunks.
const displayPct = ref<number>(clamp(props.percentage));
let raf = 0;

function tick(): void {
  const target = clamp(props.percentage);
  const diff = target - displayPct.value;
  if (Math.abs(diff) < 0.5) {
    displayPct.value = target;
  } else {
    displayPct.value += diff * 0.18;
    raf = requestAnimationFrame(tick);
  }
}

function clamp(v: number): number {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 100) return 100;
  return v;
}

watch(
  () => props.percentage,
  () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(tick);
  },
);

onBeforeUnmount(() => cancelAnimationFrame(raf));

const displayText = computed<string>(() =>
  props.isCancelling ? "Canceling..." : props.text,
);

const displayPctLabel = computed<string>(
  () => `${Math.round(displayPct.value)}%`,
);
</script>

<template>
  <div class="cf-progress" role="alertdialog" aria-modal="true">
    <div
      class="cf-progress__panel"
      :class="{ 'cf-progress__panel--with-actions': canCancel }"
    >
      <h2 class="cf-progress__title">{{ displayText }}</h2>

      <div class="cf-progress__bar cf-frosted-inversed">
        <div
          class="cf-progress__fill"
          :style="{ width: `${displayPct}%` }"
          :class="{ 'cf-progress__fill--cancelling': isCancelling }"
        />
      </div>

      <div class="cf-progress__meta">
        <span class="cf-progress__pct">{{ displayPctLabel }}</span>
        <span v-if="logText" class="cf-progress__log">{{ logText }}</span>
      </div>

      <div v-if="canCancel" class="cf-progress__actions">
        <Button
          variant="grey"
          :disabled="isCancelling"
          @click="emit('cancel')"
        >
          Cancel
        </Button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cf-progress {
  position: fixed;
  inset: 0;
  z-index: 900;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  backdrop-filter: var(--cf-panel-blur);
  -webkit-backdrop-filter: var(--cf-panel-blur);
}

:global(.cf-progress-blur-enter-active),
:global(.cf-progress-blur-leave-active) {
  animation-duration: 650ms;
  animation-timing-function: var(--cf-ease-default);
  animation-fill-mode: both;
}

:global(.cf-progress-blur-enter-active) {
  animation-name: cf-progress-blur-in;
}

:global(.cf-progress-blur-leave-active) {
  animation-name: cf-progress-blur-out;
}

@keyframes cf-progress-blur-in {
  from {
    backdrop-filter: blur(0) saturate(100%);
    -webkit-backdrop-filter: blur(0) saturate(100%);
  }

  to {
    backdrop-filter: var(--cf-panel-blur);
    -webkit-backdrop-filter: var(--cf-panel-blur);
  }
}

@keyframes cf-progress-blur-out {
  from {
    backdrop-filter: var(--cf-panel-blur);
    -webkit-backdrop-filter: var(--cf-panel-blur);
  }

  to {
    backdrop-filter: blur(0) saturate(100%);
    -webkit-backdrop-filter: blur(0) saturate(100%);
  }
}

:global(.cf-progress-blur-enter-from),
:global(.cf-progress-blur-leave-to) {
  backdrop-filter: blur(0) saturate(100%);
  -webkit-backdrop-filter: blur(0) saturate(100%);
}

/*
 * The panel fades and defocuses — NO scale, and deliberately not the shared
 * `cf-dialog` recipe in `tokens.css`. A loading screen is not a dialog: it is
 * not something the user summoned and can dismiss, it is the app telling them
 * to wait, and giving it a modal's entrance makes an unavoidable wait read as
 * an interruption. It also keeps its own 650ms and the house ease, where a
 * dialog's snappier, mirrored-easing dismissal would read as a flicker between
 * two long operations. Do not "unify" these two motions.
 */
:global(.cf-progress-blur-enter-active) .cf-progress__panel,
:global(.cf-progress-blur-leave-active) .cf-progress__panel {
  transition: opacity 650ms var(--cf-ease-default),
    filter 650ms var(--cf-ease-default);
}

:global(.cf-progress-blur-enter-from) .cf-progress__panel,
:global(.cf-progress-blur-leave-to) .cf-progress__panel {
  opacity: 0;
  filter: blur(14px);
}

:global(.cf-progress-blur-enter-to) .cf-progress__panel,
:global(.cf-progress-blur-leave-from) .cf-progress__panel {
  opacity: 1;
  filter: blur(0);
}

.cf-progress__panel {
  display: flex;
  flex-direction: column;
  gap: var(--cf-space-5);
  width: 500px;
  max-width: calc(100% - var(--cf-space-9));
  padding: var(--cf-space-7) var(--cf-space-7) var(--cf-space-6);
  background: transparent;
  border: none;
  box-shadow: none;
}

.cf-progress__title {
  margin: 0;
  font-size: var(--cf-font-size-lg);
  font-weight: var(--cf-font-weight-medium);
  color: var(--cf-color-text);
  text-align: center;
}

/*
 * The Cancel button hangs ~38px under the meta row, which puts the panel's
 * mass below the bar. The panel is centred in the viewport, so what the eye
 * reads is the BAR sitting high with the title crammed against it. Reserving
 * matching room above the title moves the title up and puts the bar back on
 * the panel's own centre line.
 *
 * Only when the button is there: without it (the scene overlay) the stack is
 * already balanced — bar centre 36px against a content centre of 34px — so an
 * unconditional gap would tip it the other way. `--cf-space-10` (40px) leaves
 * the bar ~3px above centre where 46px would be exact; the residual is well
 * inside what reads as symmetrical, and a token beats hand-fitted arithmetic
 * that silently stops being true when the button's height changes.
 */
.cf-progress__panel--with-actions .cf-progress__title {
  margin-bottom: var(--cf-space-10);
}

/*
 * The rail is EMBOSSED and the fill is RAISED, which is what makes the fill
 * read as sitting in a groove rather than as a coloured strip beside a grey
 * one. The rail's recess comes from the global `.cf-frosted-inversed` (see
 * `tokens.css`) rather than a local shadow, so the treatment stays one thing
 * wherever it is reused; the fill keeps `--cf-shadow-button`, the house glass
 * recipe (`Button.vue --grey`, the AI debug panel's actions and tab strip).
 * Shadow only on both — neither `background` changed.
 *
 * Two consequences of the rail's `overflow: hidden`, both wanted: the groove
 * is visible only where the fill has not reached, and the fill's drop shadow
 * survives only at its LEADING edge, which is exactly where it earns its keep.
 */
.cf-progress__bar {
  position: relative;
  width: 100%;
  height: 6px;
  border-radius: var(--cf-radius-3);
  background: var(--cf-color-bg-elev-1);
  overflow: hidden;
}

.cf-progress__fill {
  height: 100%;
  background: var(--cf-color-accent);
  border-radius: var(--cf-radius-3);
  box-shadow: var(--cf-shadow-button);
  transition: width 80ms linear;
}

.cf-progress__fill--cancelling {
  background: var(--cf-color-text-muted);
}

.cf-progress__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--cf-font-size-sm);
  color: var(--cf-color-text-muted);
  min-height: 16px;
}

/*
 * Deliberately NOT `font-variant-numeric: tabular-nums`, here or on the log
 * line below. Greycliff CF's tabular figures carry a much wider advance than
 * its proportional ones, so the feature reads as stray letter-spacing inside
 * the numbers while the words beside them ("of", "MB") sit normally —
 * "3 1.9 of 1 0 2.4 MB". The cost is that a counting readout shifts by a
 * fraction of a character as digits change; the meta row is
 * `space-between`, so the percentage is anchored left and the readout right
 * and neither can push the other around.
 */
.cf-progress__pct {
  white-space: nowrap;
}

.cf-progress__log {
  flex: 1 1 auto;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-left: var(--cf-space-4);
}

.cf-progress__actions {
  display: flex;
  justify-content: center;
  margin-top: var(--cf-space-3);
}
</style>
